// MARK:- OS 2.0 web component for the product detail form (variant selection,
// subscription, quantity, add-to-cart). Wraps the <form> in main-product and
// owns every interactive bit of the PDP buying flow.
//
// File naming: standalone web components in this theme use the `component-`
// prefix in the FILE NAME so they sort together in assets/. Custom element name,
// JS class, and data attributes stay short — `<as-product-form>`, AsProductForm.
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Loaded once
// per page from sections/main-product.liquid via:
//
//   <script src="{{ 'component-product-form.js' | asset_url }}" type="module"></script>
//
// MARK:- Gift-bow hooks: [data-as-bow-row] contains the nameless [data-as-bow-input] checkbox with variant ID and price.
// Markup contract:
//
//   <as-product-form data-product-id="…">
//     <script type="application/json" data-product-json>{{ product | json }}</script>
//     <form id="product-form" action="/cart/add" method="post">
//       <input type="hidden" name="id" value="…" data-as-variant-id>
//
//       <input type="radio" name="option-1" value="…" data-as-option-input data-as-option-position="1">
//       …
//
//       <input type="radio" name="purchase-type" value="one-time" data-as-purchase-type="one-time">
//       <input type="radio" name="purchase-type" value="subscription" data-as-purchase-type="subscription">
//       <input type="radio" name="subscription-interval" value="{selling_plan_id}" data-as-interval-input data-as-interval-label="…">
//       <span data-as-interval-selected>Every 4 weeks</span>
//       <input type="hidden" name="selling_plan" value="" data-as-selling-plan>
//       <span data-as-price-one-time>$28</span>
//       <span data-as-price-subscription>$28</span>
//
//       <div data-as-quantity-picker>
//         <button type="button" data-as-qty-decrement>…</button>
//         <input type="number" data-as-qty-input>
//         <button type="button" data-as-qty-increment>…</button>
//       </div>
//
//       <button type="submit" data-as-add-button>
//         <span data-as-add-text>Add to basket</span>
//         <span data-as-add-price>$28</span>
//       </button>
//     </form>
//   </as-product-form>
//
// Behavior:
// - Variant selection: option inputs change → resolve matching variant by option
//   values → update price labels, availability (disable add button), and the
//   hidden id input.
// - Subscription: the picker (snippets/product-subscription.liquid) only renders
//   when the variant has real selling_plan_allocations. Each interval radio's
//   value IS its selling_plan_id, so the hidden selling_plan input just mirrors
//   the checked radio. Subscription price comes from the matching allocation
//   (carries the discount); one-time price tracks the variant unit price.
// - Quantity: decrement/increment buttons step the input. Both pickers (initial +
//   sticky bar) sync via this single component.
// - Add-to-cart: intercept submit, fetch /cart/add.js, dispatch cart:added on
//   success (existing cart drawer listens for that), surface error inline.
// - Customizer-safe: self-init via connectedCallback. Shopify mounts/unmounts
//   the element when the section is added/removed; component re-binds correctly.

// MARK:- Shopify's hard-coded cart-cap strings. Both fire from /cart/add.js when a customer hits the per-line limit (one is "added < requested", the other is "already at max"). Substitute settings.cart_quantity_max_message for either.
const SHOPIFY_LIMIT_RE = /(?:Only (\d+) items? were added to your cart due to availability|The maximum quantity of this item is already in your cart)\./i;

class AsProductForm extends HTMLElement {
  connectedCallback() {
    this.product = this.parseProductJson();
    this.form = this.querySelector('form[data-as-product-form]');
    if (!this.product || !this.form) return;

    // MARK:- Plan IDs come from shop-rendered Liquid, never from the customer's timezone.
    try {
      this.preorderPlans = JSON.parse(this.querySelector('[data-preorder-plans]')?.textContent || '{}');
    } catch {
      this.preorderPlans = {};
    }
    this.cacheRefs();
    this.bindEvents();
    this.syncSubscriptionUi();
    this.setupFooterTuck();
    // MARK:- Preorder layout keeps the bar fixed and reveals it on scroll; the mobile dock/pin model doesn't apply.
    this.barMode = !!this.closest('.product-detail--bar');
    if (this.barMode) {
      this.setupPreorderBarReveal();
    } else {
      this.setupDockPin();
    }
  }

  disconnectedCallback() {
    if (this.onBarScroll) window.removeEventListener('scroll', this.onBarScroll);
    if (this.footerObserver) this.footerObserver.disconnect();
    if (this.pinObserver) this.pinObserver.disconnect();
    if (this.onPinResize) window.removeEventListener('resize', this.onPinResize);
    clearTimeout(this.pinResizeTimer);
  }

  // MARK:- Preorder bar reveal: hidden while the full-viewport hero is at scroll 0, .is-revealed from the first scrolled pixel (CSS in component-product-form.css). Runs once on load so a mid-page reload starts revealed.
  setupPreorderBarReveal() {
    if (!this.stickyBar) return;
    this.onBarScroll = () => {
      this.stickyBar.classList.toggle('is-revealed', window.scrollY > 0);
    };
    this.onBarScroll();
    window.addEventListener('scroll', this.onBarScroll, { passive: true });
  }

  // MARK:- Mobile persistent buy-bar: tuck the fixed bar (.is-tucked) only while the footer is in view so it never covers footer content. The class also toggles on desktop but its CSS lives inside component-product-form.css's <=960px block, so desktop stays on legacy theme.js behavior.
  setupFooterTuck() {
    const footer = document.querySelector('footer');
    if (!this.stickyBar || !footer) return;
    this.footerObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.stickyBar.classList.toggle('is-tucked', entry.isIntersecting);
    });
    this.footerObserver.observe(footer);
  }

  // MARK:- Mobile dock/pin buy-bar: bar starts in flow (position:static <=960px in component-product-form.css); .is-pinned (fixed bottom) toggles on once the bar's slot — the zero-height [data-as-bar-sentinel] before it — has scrolled fully into view, and off when scrolling back above it. rootMargin raises the observed bottom edge by the bar height so IO fires exactly when the docked bar's bottom meets the viewport bottom; observer is rebuilt on resize since that height is baked into rootMargin. Class is inert on desktop (CSS lives in the <=960px block).
  setupDockPin() {
    this.barSentinel = this.querySelector('[data-as-bar-sentinel]');
    if (!this.stickyBar || !this.barSentinel) return;

    const observe = () => {
      if (this.pinObserver) this.pinObserver.disconnect();
      const barHeight = this.stickyBar.offsetHeight;
      this.pinObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        const pin = entry.boundingClientRect.top < window.innerHeight - barHeight;
        this.stickyBar.classList.toggle('is-pinned', pin);
      }, { rootMargin: `0px 0px -${barHeight}px 0px` });
      this.pinObserver.observe(this.barSentinel);
    };

    observe();

    this.onPinResize = () => {
      clearTimeout(this.pinResizeTimer);
      this.pinResizeTimer = setTimeout(observe, 150);
    };
    window.addEventListener('resize', this.onPinResize);
  }

  parseProductJson() {
    const node = this.querySelector('script[data-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('[as-product-form] Could not parse product JSON', err);
      return null;
    }
  }

  cacheRefs() {
    // MARK:- Gift-bow add-on row (snippets/product-gift-bow.liquid). Absent when the product has no add-on entry.
    this.bowRow = this.querySelector('[data-as-bow-row]');
    this.bowInput = this.querySelector('[data-as-bow-input]');
    this.bowPrice = this.bowInput ? parseInt(this.bowInput.dataset.asBowPrice, 10) || 0 : 0;
    this.variantIdInput = this.querySelector('[data-as-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-as-selling-plan]');
    this.optionInputs = Array.from(this.querySelectorAll('[data-as-option-input]'));
    this.purchaseRadios = Array.from(this.querySelectorAll('[data-as-purchase-type]'));
    this.intervalInputs = Array.from(this.querySelectorAll('[data-as-interval-input]'));
    this.intervalSelectedLabel = this.querySelector('[data-as-interval-selected]');
    this.priceOneTime = this.querySelector('[data-as-price-one-time]');
    this.priceSubscription = this.querySelector('[data-as-price-subscription]');
    // MARK:- Compare-at strike-through siblings (toggled via [hidden] on variant change)
    this.compareAtOneTime = this.querySelector('[data-as-compare-at-one-time]');
    this.compareAtSubscription = this.querySelector('[data-as-compare-at-subscription]');
    this.compareAtAdd = this.querySelector('[data-as-compare-at-add]');
    this.stickyBar = this.querySelector('[data-as-sticky-bar]');
    this.addButton = this.querySelector('[data-as-add-button]');
    this.addText = this.querySelector('[data-as-add-text]');
    this.addPrice = this.querySelector('[data-as-add-price]');
    this.qtyInputs = Array.from(this.querySelectorAll('[data-as-qty-input]'));
    this.qtyDecrements = Array.from(this.querySelectorAll('[data-as-qty-decrement]'));
    this.qtyIncrements = Array.from(this.querySelectorAll('[data-as-qty-increment]'));
    this.qtyLiveRegions = Array.from(this.querySelectorAll('[data-as-qty-live]'));
    // MARK:- Visible "Limit X per order" indicator next to the QUANTITY label. Hidden in the snippet by default; shown when atMax (refreshQtyButtonState) or after a SHOPIFY_LIMIT_RE-matching 422 (showError). Resolved text lives on data-as-qty-limit-message.
    this.qtyLimitTexts = Array.from(this.querySelectorAll('[data-as-qty-limit-text]'));
    // MARK:- Quantity limits — per-product `data-as-qty-max` on <as-product-form> wins over the body-level theme cap. Per-product value comes from product.metafields.product.quantity_limit (set in main-product.liquid). Empty/missing => fall back to body, which carries settings.cart_quantity_max. Infinity = no theme cap; Shopify still enforces 50 server-side.
    this.qtyMin = parseInt(document.body.dataset.asQtyMin, 10) || 1;
    const rawMax = parseInt(this.dataset.asQtyMax || document.body.dataset.asQtyMax, 10);
    this.qtyMax = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : Infinity;
    this.qtyMessage = document.body.dataset.asQtyMessage || '';
    this.multiplyAddPrice = !!(this.addButton && this.addButton.hasAttribute('data-as-multiply-price'));
    this.currentVariant = this.product.variants.find((v) => String(v.id) === String(this.variantIdInput && this.variantIdInput.value)) || this.product.variants[0];
    this.refreshQtyButtonState();
  }

  bindEvents() {
    // MARK:- Bow selection updates the buy-button total and compare-at price.
    if (this.bowInput) this.bowInput.addEventListener('change', () => { this.refreshAddPrice(); this.refreshCompareAtAdd(); });
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange());
    });

    this.purchaseRadios.forEach((input) => {
      input.addEventListener('change', () => this.syncSubscriptionUi());
    });

    this.intervalInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleIntervalChange());
    });

    this.qtyDecrements.forEach((btn) => btn.addEventListener('click', () => this.adjustQty(-1)));
    this.qtyIncrements.forEach((btn) => btn.addEventListener('click', () => this.adjustQty(1)));
    this.qtyInputs.forEach((input) => input.addEventListener('input', (e) => this.syncQty(e.target)));
    // MARK:- Keyboard parity for the readonly input — ArrowUp / ArrowDown mirror the +/- buttons so screen-reader/keyboard users keep native qty UX.
    this.qtyInputs.forEach((input) => input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); this.adjustQty(1); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); this.adjustQty(-1); }
    }));

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  handleOptionChange() {
    const selected = this.collectSelectedOptions();
    const variant = this.findVariantByOptions(selected);
    if (!variant) return;
    this.applyVariant(variant);
  }

  collectSelectedOptions() {
    const byPosition = {};
    this.optionInputs.forEach((input) => {
      if (!input.checked) return;
      const pos = parseInt(input.dataset.asOptionPosition, 10);
      byPosition[pos] = input.value;
    });
    return byPosition;
  }

  findVariantByOptions(byPosition) {
    return this.product.variants.find((variant) => {
      return variant.options.every((value, index) => {
        const expected = byPosition[index + 1];
        return expected == null || expected === value;
      });
    });
  }

  applyVariant(variant) {
    this.currentVariant = variant;
    // MARK:- Resolve the new variant's locked plan before refreshing prices.
    this.refreshSellingPlanForVariant();
    if (this.variantIdInput) this.variantIdInput.value = variant.id;

    // MARK:- One-time price + compare-at follow the variant unit price
    const unitPriceFormatted = this.formatMoney(variant.price);
    if (this.priceOneTime) this.priceOneTime.textContent = unitPriceFormatted;
    const onSale = !!variant.compare_at_price && variant.compare_at_price > variant.price;
    if (this.compareAtOneTime) {
      if (onSale) {
        this.compareAtOneTime.textContent = this.formatMoney(variant.compare_at_price);
        this.compareAtOneTime.removeAttribute('hidden');
      } else {
        this.compareAtOneTime.setAttribute('hidden', '');
      }
    }
    // MARK:- Subscription price comes from the matching selling-plan allocation (carries the discount), not the unit price
    this.refreshSubscriptionPrice();
    this.refreshAddPrice();
    this.refreshCompareAtAdd();

    this.refreshPurchaseAvailability();
  }

  // MARK:- Effective unit price + compare-at for the buy button. In subscription mode it reflects the selected allocation's discounted price; otherwise the variant unit price.
  currentAddPricing() {
    if (this.classList.contains('is-subscription') || this.sellingPlanInput?.hasAttribute('data-as-plan-locked')) {
      const allocation = this.currentAllocation();
      if (allocation) {
        return { price: allocation.price, compareAt: allocation.compare_at_price };
      }
    }
    const variant = this.currentVariant;
    return { price: variant ? variant.price : 0, compareAt: variant ? variant.compare_at_price : 0 };
  }

  refreshAddPrice() {
    if (!this.addPrice || !this.currentVariant) return;
    const qty = this.multiplyAddPrice ? this.currentQuantity() : 1;
    // MARK:- Selected bow follows the displayed parent quantity.
    const bow = this.bowSelected() ? this.bowPrice * qty : 0;
    this.addPrice.textContent = this.formatMoney(this.currentAddPricing().price * qty + bow);
  }

  // MARK:- Mirror refreshAddPrice() for the compare-at strike on the buy-button
  refreshCompareAtAdd() {
    if (!this.compareAtAdd || !this.currentVariant) return;
    const { price, compareAt } = this.currentAddPricing();
    const onSale = !!compareAt && compareAt > price;
    if (!onSale) {
      this.compareAtAdd.setAttribute('hidden', '');
      return;
    }
    const qty = this.multiplyAddPrice ? this.currentQuantity() : 1;
    // MARK:- Include the same one-time bow amount in the compare-at total.
    const bow = this.bowSelected() ? this.bowPrice * qty : 0;
    this.compareAtAdd.textContent = this.formatMoney(compareAt * qty + bow);
    this.compareAtAdd.removeAttribute('hidden');
  }

  // MARK:- Bow counts only when checked AND visible (syncSubscriptionUi hides + unchecks it under Hide-for-subscriptions).
  bowSelected() {
    return !!(this.bowInput && this.bowInput.checked && this.bowRow && !this.bowRow.hidden);
  }

  currentQuantity() {
    const input = this.qtyInputs[0];
    if (!input) return 1;
    const parsed = parseInt(input.value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  syncSubscriptionUi() {
    const selected = this.purchaseRadios.find((input) => input.checked);
    const isSubscription = selected && selected.dataset.asPurchaseType === 'subscription';
    this.classList.toggle('is-subscription', !!isSubscription);
    this.refreshSellingPlanForVariant();
    this.refreshPurchaseAvailability();
    // MARK:- Hide-for-subscriptions valve (settings.cart_add_on_hide_for_subscriptions): hide + uncheck the bow row in subscription mode so it can never submit. Runs on connect too, covering requires_selling_plan products.
    if (this.bowRow && this.bowRow.dataset.asBowHideForSubscriptions === 'true') {
      this.bowRow.hidden = !!isSubscription;
      if (isSubscription && this.bowInput) this.bowInput.checked = false;
    }
    // MARK:- Buy-button price follows the selected purchase type (subscription = discounted)
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  handleIntervalChange() {
    const selected = this.intervalInputs.find((input) => input.checked);
    if (this.intervalSelectedLabel && selected) {
      this.intervalSelectedLabel.textContent = selected.dataset.asIntervalLabel || selected.value;
    }
    this.refreshSubscriptionPrice();
    this.refreshSellingPlanForVariant();
    // MARK:- A different frequency can carry a different allocation price, so refresh the buy button too
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  // MARK:- The checked interval radio's value IS the selling_plan_id (server-rendered from the variant's allocations), so the hidden input just mirrors it in subscription mode.
  refreshSellingPlanForVariant() {
    if (!this.sellingPlanInput) return;
    // MARK:- Date-controlled preorders use the exact STOQ allocation for the selected variant.
    if (this.sellingPlanInput.hasAttribute('data-as-plan-locked')) {
      this.sellingPlanInput.value = this.preorderPlans[String(this.currentVariant?.id)] || '';
      return;
    }
    if (!this.classList.contains('is-subscription')) {
      this.sellingPlanInput.value = '';
      return;
    }
    const checked = this.intervalInputs.find((input) => input.checked);
    this.sellingPlanInput.value = (checked && checked.value) || '';
  }

  // MARK:- Resolve the allocation for the current variant + selected selling_plan_id. Falls back to the first allocation so the seeded price stays sensible.
  currentAllocation() {
    const variant = this.currentVariant || this.product.variants.find((v) => String(v.id) === String(this.variantIdInput && this.variantIdInput.value));
    const allocations = (variant && variant.selling_plan_allocations) || [];
    if (allocations.length === 0) return null;
    // MARK:- Never fall back to an arbitrary allocation for a locked preorder plan.
    if (this.sellingPlanInput?.hasAttribute('data-as-plan-locked')) {
      return allocations.find((allocation) => String(allocation.selling_plan_id) === this.sellingPlanInput.value) || null;
    }
    const checked = this.intervalInputs.find((input) => input.checked);
    const match = checked && allocations.find((a) => String(a.selling_plan_id) === String(checked.value));
    return match || allocations[0];
  }

  // MARK:- Subscription price + compare-at reflect the matching allocation (discounted price), refreshed on variant change and interval change.
  refreshSubscriptionPrice() {
    const allocation = this.currentAllocation();
    if (!allocation) return;
    if (this.priceSubscription) this.priceSubscription.textContent = this.formatMoney(allocation.price);
    if (this.compareAtSubscription) {
      const onSale = !!allocation.compare_at_price && allocation.compare_at_price > allocation.price;
      if (onSale) {
        this.compareAtSubscription.textContent = this.formatMoney(allocation.compare_at_price);
        this.compareAtSubscription.removeAttribute('hidden');
      } else {
        this.compareAtSubscription.setAttribute('hidden', '');
      }
    }
  }

  adjustQty(delta) {
    this.qtyInputs.forEach((input) => {
      const raw = parseInt(input.value, 10) + delta;
      const next = Math.min(this.qtyMax, Math.max(this.qtyMin, raw));
      input.value = next;
    });
    this.refreshQtyButtonState();
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  syncQty(source) {
    const raw = parseInt(source.value, 10) || this.qtyMin;
    const next = Math.min(this.qtyMax, Math.max(this.qtyMin, raw));
    source.value = next;
    this.qtyInputs.forEach((input) => {
      if (input !== source) input.value = next;
    });
    this.refreshQtyButtonState();
    this.refreshAddPrice();
    this.refreshCompareAtAdd();
  }

  // MARK:- Toggle native + aria disabled on every +/- button based on current qty vs theme limits, announce the merchant message via the aria-live regions when at max, and toggle the visible "Limit X per order" span next to the label. Called after every adjust/sync and once at end of cacheRefs() for first-paint accuracy.
  refreshQtyButtonState() {
    const value = this.currentQuantity();
    const atMin = value <= this.qtyMin;
    const atMax = value >= this.qtyMax;
    this.qtyDecrements.forEach((btn) => {
      btn.disabled = atMin;
      if (atMin) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
    this.qtyIncrements.forEach((btn) => {
      btn.disabled = atMax;
      if (atMax) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
    const announcement = atMax && this.qtyMessage ? this.qtyMessage : '';
    this.qtyLiveRegions.forEach((el) => { el.textContent = announcement; });
    // MARK:- Visible limit indicator follows atMax. Empty + hidden when below max.
    this.qtyLimitTexts.forEach((el) => {
      if (atMax) {
        const msg = el.getAttribute('data-as-qty-limit-message') || '';
        el.textContent = msg;
        if (msg) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      } else {
        el.textContent = '';
        el.setAttribute('hidden', '');
      }
    });
  }

  // MARK:- Missing preorder plans stay unpurchasable through variant/quantity updates and failed requests.
  canPurchase() {
    if (!this.currentVariant?.available) return false;
    if (this.dataset.asPreorder === 'true') return !!this.currentAllocation();
    if (this.currentVariant.requires_selling_plan && !this.sellingPlanInput?.value) return false;
    return true;
  }

  refreshPurchaseAvailability() {
    const available = this.canPurchase();
    if (this.addButton) this.addButton.disabled = !available;
    if (this.addText) {
      this.addText.textContent = !this.currentVariant?.available
        ? this.addText.dataset.asSoldOutText
        : available ? this.addText.dataset.asAvailableText : this.addText.dataset.asUnavailableText;
    }
  }

  // MARK:- Re-render the product section before buying a cutoff-configured product. The shop decides
  // time/state; Date.now below is only a cache buster. Changed terms reload before any cart mutation.
  async verifyPreorderPurchase() {
    if (!Number(this.dataset.asPreorderCutoff)) return true;
    const requestedVariant = String(this.currentVariant.id);
    const url = new URL(this.dataset.asProductUrl, window.location.origin);
    url.searchParams.set('section_id', this.dataset.asSectionId);
    url.searchParams.set('variant', this.currentVariant.id);
    url.searchParams.set('_preorder_check', Date.now());
    const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error('Could not verify purchase availability. Please try again.');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.querySelector('as-product-form');
    if (!fresh) throw new Error('Could not verify purchase availability. Please refresh this page.');
    if (String(this.currentVariant.id) !== requestedVariant) {
      throw new Error('Your selection changed. Please add your selection again.');
    }
    const freshPlan = fresh.dataset.asPreorderPlan || '';
    const currentPlan = this.dataset.asPreorder === 'true' ? this.sellingPlanInput?.value || '' : '';
    if (fresh.dataset.asPreorder !== this.dataset.asPreorder || freshPlan !== currentPlan) {
      const destination = new URL(this.dataset.asProductUrl, window.location.origin);
      destination.searchParams.set('variant', this.currentVariant.id);
      window.location.assign(destination.href);
      return false;
    }
    if (fresh.querySelector('[data-as-add-button]')?.disabled) {
      throw new Error('This purchase option is currently unavailable. Please refresh this page.');
    }
    return true;
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting || !this.canPurchase()) return;
    this.submitting = true;
    if (this.addButton) this.addButton.disabled = true;
    // MARK:- Availability checks must finish before FormData is posted or any cart action runs.
    try {
      if (!await this.verifyPreorderPurchase()) {
        this.submitting = false;
        return;
      }
    } catch (error) {
      this.showError(error.message);
      this.submitting = false;
      this.refreshPurchaseAvailability();
      return;
    }
    const formData = new FormData(this.form);
    // MARK:- Snapshot bow selection after the preorder availability check.
    const bowChecked = this.bowSelected();
    try {
      // MARK:- Use the configured actions handler for standard events; properties, selling plans, or a nested bow require the direct path because updateCart cannot express them.
      const canRoute = window.__asActionsConfigured && window.__asActionsConfigured.updateCart
        && window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.updateCart === 'function';
      const hasExtras = [...formData.keys()].some((k) => k.startsWith('properties[') || k === 'selling_plan');
      const routedId = formData.get('id');
      if (canRoute && !hasExtras && routedId && !bowChecked) {
        const variantId = String(routedId);
        const payload = { lines: [{ merchandiseId: variantId, quantity: parseInt(formData.get('quantity'), 10) || 1 }] };
        const runsBefore = window.__asCartHandlerRuns ? window.__asCartHandlerRuns() : 0;
        let routedResult = null;
        let routedFailed = false;
        try {
          routedResult = await window.Shopify.actions.updateCart(
            payload,
            { event: { context: 'product', detail: { source: 'product-form', variantId } } },
          );
        } catch (actionErr) {
          // MARK:- A rejection AFTER our handler ran means the mutation applied and the actions runtime failed post-hoc — sync the UI, never show the raw runtime error. A pre-handler rejection means nothing happened; fall through to the direct path.
          console.error('[as-product-form] updateCart action', actionErr);
          const handled = (window.__asCartPayloadHandled && window.__asCartPayloadHandled(payload))
            || (window.__asCartHandlerRuns && window.__asCartHandlerRuns() > runsBefore);
          if (handled) {
            this.clearCartDrawerError();
            this.refreshQtyButtonState();
            this.openCartDrawer();
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }
          routedFailed = true;
        }
        if (!routedFailed) {
          if (routedResult && routedResult.userErrors && routedResult.userErrors.length) {
            // MARK:- Mirror the direct catch path. The handler already put the error on the matching cart line (detail.variantId → showLineError); here we surface the PDP-side UX and re-sync the drawer.
            const message = (routedResult.userErrors[0] && routedResult.userErrors[0].message) || '';
            this.showError(message);
            this.eagerlyCorrectCartLine(message);
            this.openCartDrawer();
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            this.clearCartDrawerError();
            this.refreshQtyButtonState();
            this.openCartDrawer();
          }
          return;
        }
      }

      // MARK:- Request the cart-drawer section render in the same round trip so component-cart.js can apply it without a follow-up /cart/update.js fetch.
      let response;
      if (bowChecked) {
        // MARK:- Nested add: parent + bow in ONE /cart/add.js request; parent_id = the parent's variant id in the same request (Shopify nested cart lines). The child never carries selling_plan — the bow is one-time even under a subscription parent. Actions payload has no parent field, so this is always the direct path.
        const parent = this.formDataToItem(formData);
        // MARK:- Hidden _gift_bow property keeps a bow-carrying parent as its own cart line, so a later plain add of the same variant (or a plain line already in the cart) never merges with it. Bow qty follows the parent, so merging would silently bow the whole line.
        parent.properties = { ...(parent.properties || {}), _gift_bow: '1' };
        const items = [parent, { id: String(this.bowInput.dataset.asBowVariantId), quantity: parent.quantity, parent_id: parent.id }];
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: 'cart-drawer' }),
        });
      } else {
        formData.append('sections', 'cart-drawer');
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || 'Could not add to basket.');
      }
      const data = await response.json();
      // MARK:- Successful add clears any stale cart-drawer error from a prior limit-hit attempt, plus re-syncs the visible limit indicator to the current qty (a previous 422 may have forced it visible while the picker is below max).
      this.clearCartDrawerError();
      this.refreshQtyButtonState();
      // Dispatch BEFORE opening: component-cart.js applies the section synchronously
      // so the drawer opens with fresh contents already in place.
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { response: data } }));
      this.openCartDrawer();
    } catch (err) {
      // MARK:- Surface the error on the PDP (label slot for limit errors, form-bottom <p> for everything else) and on the matching cart line (cart:line-error → showLineError populates the per-line indicator; falls back to the drawer-level red surface only when no line matches, e.g. brand-new add hits limit on first try). Eagerly correct the matching cart line so the drawer reflects reality the moment it opens; cart:refresh then confirms.
      this.showError(err.message);
      this.eagerlyCorrectCartLine(err.message);
      const variantId = this.variantIdInput && this.variantIdInput.value;
      if (variantId) {
        document.dispatchEvent(new CustomEvent('cart:line-error', { detail: { variantId, message: err.message } }));
      }
      this.openCartDrawer();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      // MARK:- Restore the real availability, including the locked preorder allocation.
      this.submitting = false;
      this.refreshPurchaseAvailability();
    }
  }

  // MARK:- FormData → /cart/add.js JSON item. Only cart fields survive: id, quantity, selling_plan (non-empty), properties[x] → properties.x. UI-only inputs (option-N, purchase-type, subscription-interval) are dropped.
  formDataToItem(formData) {
    const item = { id: String(formData.get('id')), quantity: parseInt(formData.get('quantity'), 10) || 1 };
    const plan = formData.get('selling_plan');
    if (plan) item.selling_plan = String(plan);
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('#cart.drawer');
    if (!cartDrawer || cartDrawer.classList.contains('is-open')) return;
    const trigger = document.querySelector('[data-as-drawer="cart"]:not([data-as-drawer-hover])');
    if (trigger) trigger.click();
  }

  showError(message) {
    // MARK:- Limit-cap errors (SHOPIFY_LIMIT_RE) route to the visible "Limit X per order" span next to the QUANTITY label — that's the design surface. Other errors fall through to the inline form-bottom <p> in red. Skips the form-bottom append when the limit slot exists, so we don't double-up.
    const isLimitError = SHOPIFY_LIMIT_RE.test(message);
    if (isLimitError && this.qtyLimitTexts.length > 0) {
      this.qtyLimitTexts.forEach((el) => {
        const msg = el.getAttribute('data-as-qty-limit-message') || '';
        if (!msg) return;
        el.textContent = msg;
        el.removeAttribute('hidden');
      });
      return;
    }
    const display = (this.qtyMessage && isLimitError) ? this.qtyMessage : message;
    let inline = this.querySelector('[data-as-error]');
    if (!inline) {
      inline = document.createElement('p');
      inline.setAttribute('data-as-error', '');
      inline.setAttribute('role', 'status');
      inline.setAttribute('aria-live', 'polite');
      inline.style.color = 'var(--error, #FF5656)';
      inline.style.marginTop = '8px';
      this.form.appendChild(inline);
    }
    inline.textContent = display;
  }

  // MARK:- Clears any drawer-level error left over from a prior add attempt (e.g. component-cart.js's showLineError fallback wrote to it when no matching line existed). Per-line errors are owned by the cart line indicator and clear themselves via refreshLineQtyButtonState on the next mutation.
  clearCartDrawerError() {
    const target = document.querySelector('[data-as-cart-error]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }

  // MARK:- Shopify caps the cart server-side when a customer exceeds inventory or the theme max, but its 422 response doesn't ship the new cart state. Compute the post-add qty for the matching line ourselves: "Only N items were added" → currentQty + N (capped at theme max if set); "maximum quantity already in cart" → theme max (when set). Update the qty input + button state immediately so the drawer reflects reality the moment it opens. cart:refresh re-fetches and self-corrects if our derivation is off.
  eagerlyCorrectCartLine(message) {
    const variantId = this.variantIdInput && this.variantIdInput.value;
    if (!variantId) return;
    const line = document.querySelector(`[data-as-cart-line][data-as-cart-variant-id="${variantId}"]`);
    if (!line) return;
    const input = line.querySelector('[data-as-cart-qty-input]');
    if (!input) return;
    const onlyN = message.match(/Only (\d+) items? were added/i);
    const maxAlready = /The maximum quantity of this item is already in your cart/i.test(message);
    let target = null;
    if (onlyN) {
      const current = parseInt(input.value, 10) || 0;
      target = current + parseInt(onlyN[1], 10);
      if (this.qtyMax !== Infinity) target = Math.min(target, this.qtyMax);
    } else if (maxAlready && this.qtyMax !== Infinity) {
      target = this.qtyMax;
    }
    if (target === null || target === parseInt(input.value, 10)) return;
    input.value = target;
    const inc = line.querySelector('[data-as-cart-qty-increment]');
    const dec = line.querySelector('[data-as-cart-qty-decrement]');
    const atMax = this.qtyMax !== Infinity && target >= this.qtyMax;
    const atMin = target <= 1;
    if (inc) {
      inc.disabled = atMax;
      if (atMax) inc.setAttribute('aria-disabled', 'true');
      else inc.removeAttribute('aria-disabled');
    }
    if (dec) {
      dec.disabled = atMin;
      if (atMin) dec.setAttribute('aria-disabled', 'true');
      else dec.removeAttribute('aria-disabled');
    }
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const trimmed = value.replace(/\.00$/, '');
    return `$${trimmed}`;
  }
}

if (!customElements.get('as-product-form')) {
  customElements.define('as-product-form', AsProductForm);
}
